// Configuration for Business <> AI MBA Quiz
import React from 'react';
import {
  Briefcase,
  ChartBar,
  TrendUp,
  MegaphoneSimple,
  Package,
  Lightbulb,
  Clock,
  Timer,
  Trophy,
  Target,
  CurrencyInr,
  Rocket,
  ChartLineUp,
  Users,
  Database,
  PresentationChart,
  ShoppingCart,
  Storefront,
  Buildings,
  Code,
  CheckCircle,
  XCircle,
  Path,
  Lightning,
  Brain,
  Gear
} from 'phosphor-react';

// Screen 1: Current Role/Background
// Note: `experience` is no longer asked here — it is derived from the
// graduation year captured on the MBA landing page (see deriveExperienceFromGradYear
// in this file) and pre-seeded into the quiz responses on quiz mount.
export const MBA_INTAKE_SCREEN_1 = {
  id: 'current-role',
  initialChatText: "Welcome! Let's understand your professional background to personalize your Business <> AI assessment.",
  questions: [
    {
      id: 'currentRole',
      question: "What's your current role or background?",
      options: [
        { value: 'pm', label: 'Product / Program / Project Manager', icon: <Briefcase size={24} weight="duotone" /> },
        { value: 'finance', label: 'Finance / Business Analyst', icon: <ChartBar size={24} weight="duotone" /> },
        { value: 'sales', label: 'Sales / Growth / Revenue roles', icon: <TrendUp size={24} weight="duotone" /> },
        { value: 'marketing', label: 'Marketing / Brand / Performance Marketing', icon: <MegaphoneSimple size={24} weight="duotone" /> },
        { value: 'operations', label: 'Operations / Supply Chain / Strategy', icon: <Package size={24} weight="duotone" /> },
        { value: 'founder', label: 'Startup Founder / Entrepreneur', icon: <Lightbulb size={24} weight="duotone" /> },
        { value: 'tech', label: 'Engineering / DevOps / Tech Roles', icon: <Code size={24} weight="duotone" /> }
      ]
    }
  ],
  chatResponseMap: {
    currentRole: {
      'pm': 'Excellent! Product managers with AI/data skills are transforming how companies build and scale products.',
      'finance': 'Great choice! Finance and analytics professionals are at the forefront of AI-driven decision making.',
      'sales': 'Perfect! Sales and growth leaders who understand AI are revolutionizing revenue operations.',
      'marketing': 'Awesome! Marketing professionals leveraging AI are seeing unprecedented ROI and scale.',
      'operations': 'Fantastic! Operations and strategy roles are being redefined by AI and automation.',
      'founder': 'Impressive! Founders who master AI have a massive competitive advantage in building and scaling.',
      'tech': 'Excellent! Engineers with business acumen and AI skills are becoming strategic leaders in tech companies.'
    }
  }
};

// Maps a graduation year to the experience bucket the quiz expects.
// 2026 grad → fresh (0-3), 2018 grad → 3-8, 2014 grad → 8+
export const deriveExperienceFromGradYear = (gradYear) => {
  const year = parseInt(gradYear, 10);
  if (!year || Number.isNaN(year)) return null;
  const yearsSince = new Date().getFullYear() - year;
  if (yearsSince < 3) return '0-3';
  if (yearsSince < 8) return '3-8';
  return '8+';
};

// Screen 2: Career Goals (Multiselect)
export const MBA_INTAKE_SCREEN_2 = {
  id: 'career-goals',
  initialChatText: "Now let's understand what you're aiming to achieve. You can select multiple goals that matter to you.",
  questions: [
    {
      id: 'primaryGoal',
      question: 'What are your career goals? (Select up to 3)',
      isMultiselect: true,
      maxSelections: 3,
      minSelections: 1,
      options: [
        { value: 'ai-leadership', label: 'Move into a Business + AI leadership role', icon: <ChartLineUp size={24} weight="duotone" /> },
        { value: 'ai-pm', label: 'Become a Product Manager with strong AI/data skills', icon: <Target size={24} weight="duotone" /> },
        { value: 'analytics-strategy', label: 'Transition into Business Analytics / Strategy', icon: <ChartBar size={24} weight="duotone" /> },
        { value: 'improve-current', label: 'Improve performance & growth in current role', icon: <TrendUp size={24} weight="duotone" /> },
        { value: 'build-startup', label: 'Build / scale my own startup using AI', icon: <Rocket size={24} weight="duotone" /> },
        { value: 'salary-growth', label: 'Higher salary & faster career growth', icon: <CurrencyInr size={24} weight="duotone" /> }
      ]
    }
  ],
  chatResponseMap: {
    primaryGoal: {
      'ai-leadership': 'Ambitious! AI-fluent business leaders are the most sought-after talent right now.',
      'ai-pm': 'Smart choice! AI-native PMs command premium salaries and drive critical decisions.',
      'analytics-strategy': 'Excellent path! Strategy roles increasingly require AI/data fluency for impact.',
      'improve-current': 'Practical! AI can 10x your effectiveness and make you indispensable in your role.',
      'build-startup': 'Bold! Founders who master AI have unfair advantages in speed, scale, and capital efficiency.',
      'salary-growth': 'Valid goal! Business + AI professionals command 30-50% salary premiums.'
    }
  }
};

// Role-specific deep-dive questions
// NEW STRUCTURE: Questions are now organized by role AND experience level
// Structure: { 'role': { '0-3': [screens...], '3-8': [screens...], '8+': [screens...] } }
// Experience levels: '0-3', '3-8', '8+'
export const MBA_ROLE_SPECIFIC_SCREENS = {
  'pm': {
    // 0-3 years experience (Entry level)
    '0-3': [
      // Screen 1: PM-E1, PM-E2
      {
        id: 'pm-screen-1',
        initialChatText: "Let's dive into your product thinking and decision-making approach.",
        questions: [
          {
            id: 'pm-e1',
            question: 'Two weeks after launching a new onboarding feature, you notice that overall sign-ups are stable, but activation rate has dropped from 62% to 47%. Leadership is asking for quick fixes. What do you do first?',
            helperText: 'Tests data-driven problem-solving',
            isScenario: true,
            options: [
              { value: 'add-nudges', label: 'Add nudges and tooltips to push users forward', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'break-down-funnel', label: 'Break down the funnel step-by-step and identify exact drop-off points', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'ask-support', label: 'Ask support if they\'ve received complaints', icon: <Users size={24} weight="duotone" /> },
              { value: 'roll-back', label: 'Roll back the feature to restore previous flow', icon: <XCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-e2',
            question: 'Your sales team insists that customers need a dashboard export feature urgently. No data currently tracks how many users are requesting this. What would you do?',
            helperText: 'Tests user-centric discovery',
            isScenario: true,
            options: [
              { value: 'add-to-sprint', label: 'Immediately add it to next sprint to support sales', icon: <Rocket size={24} weight="duotone" /> },
              { value: 'interview-churned', label: 'Interview recent churned customers to validate pain', icon: <Users size={24} weight="duotone" /> },
              { value: 'ship-lightweight', label: 'Ship a lightweight version quickly to test usage', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'benchmark-competitors', label: 'Benchmark competitors and replicate best practice', icon: <ChartBar size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: PM-E3, PM-E4
      {
        id: 'pm-screen-2',
        initialChatText: "Now let's explore your prioritization and measurement thinking.",
        questions: [
          {
            id: 'pm-e3',
            question: 'You have 3 competing features: One improves user retention, one requested by enterprise client, one improves internal efficiency. Resources allow only one this sprint. How do you decide?',
            helperText: 'Tests data-driven prioritization',
            isScenario: true,
            options: [
              { value: 'choose-enterprise', label: 'Choose enterprise client request', icon: <Buildings size={24} weight="duotone" /> },
              { value: 'scoring-framework', label: 'Use a structured scoring framework (impact, effort, risk)', icon: <Target size={24} weight="duotone" /> },
              { value: 'ask-leadership', label: 'Ask leadership to decide', icon: <Users size={24} weight="duotone" /> },
              { value: 'pick-retention', label: 'Pick retention because growth is important', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-e4',
            question: 'You launched a feature meant to improve engagement. After 30 days, overall usage increased but retention stayed flat. What does this signal?',
            helperText: 'Tests strategic measurement thinking',
            isScenario: true,
            options: [
              { value: 'feature-successful', label: 'Feature is successful', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'cohort-analysis', label: 'Need deeper cohort analysis', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'increase-marketing', label: 'Increase marketing push', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'add-more-features', label: 'Add more features around it', icon: <Code size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: PM-E5, PM-E6
      {
        id: 'pm-screen-3',
        initialChatText: "Finally, let's assess your AI leverage and strategic thinking.",
        questions: [
          {
            id: 'pm-e5',
            question: 'You spend 6 hours weekly synthesizing user feedback and converting it into insights. How would you use AI here?',
            helperText: 'Tests AI literacy and workflow optimization',
            options: [
              { value: 'auto-summarize', label: 'Auto-summarize feedback themes and cluster patterns', icon: <Brain size={24} weight="duotone" /> },
              { value: 'generate-prd', label: 'Generate PRD drafts from raw notes', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'respond-stakeholders', label: 'Use AI to respond to stakeholders', icon: <Users size={24} weight="duotone" /> },
              { value: 'not-use-ai', label: 'Not use AI; prefer manual review', icon: <Gear size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-e6',
            question: 'Quantitative data shows high feature engagement, but user interviews reveal frustration and confusion. What\'s your next step?',
            helperText: 'Tests strategic thinking and data synthesis',
            isScenario: true,
            options: [
              { value: 'ignore-interviews', label: 'Ignore interviews since data is strong', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'segment-analyze', label: 'Segment users and analyze qualitative patterns deeper', icon: <Users size={24} weight="duotone" /> },
              { value: 'redesign-immediately', label: 'Redesign immediately', icon: <Code size={24} weight="duotone" /> },
              { value: 'wait-month', label: 'Wait another month', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level)
    '3-8': [
      // Screen 1: PM-M1, PM-M2
      {
        id: 'pm-screen-1',
        initialChatText: "Let's dive into your product strategy and leadership approach.",
        questions: [
          {
            id: 'pm-m1',
            question: 'Your product\'s monthly active users have grown 35% YoY, but revenue growth is flat. Leadership wants to introduce aggressive monetization (paywalls + upsells). What do you do first?',
            helperText: 'Tests product strategy and monetization thinking',
            isScenario: true,
            options: [
              { value: 'implement-monetization', label: 'Implement monetization quickly to capture value', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'ltv-analysis', label: 'Run cohort-level LTV analysis before changing pricing', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'upsell-power-users', label: 'Add upsell prompts to power users only', icon: <Target size={24} weight="duotone" /> },
              { value: 'delay-monetization', label: 'Delay monetization to protect growth', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-m2',
            question: 'Engineering says technical debt is slowing delivery by 40%. Sales says missing features are hurting enterprise deals. Resources are limited. What do you do?',
            helperText: 'Tests leadership and conflict resolution',
            isScenario: true,
            options: [
              { value: 'prioritize-enterprise', label: 'Prioritize enterprise features', icon: <Buildings size={24} weight="duotone" /> },
              { value: 'prioritize-refactor', label: 'Prioritize refactor', icon: <Code size={24} weight="duotone" /> },
              { value: 'quantify-phased', label: 'Quantify impact of both and create phased roadmap', icon: <ChartLineUp size={24} weight="duotone" /> },
              { value: 'escalate-leadership', label: 'Escalate to leadership', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: PM-M3, PM-M4
      {
        id: 'pm-screen-2',
        initialChatText: "Now let's explore your strategic alignment and AI approach.",
        questions: [
          {
            id: 'pm-m3',
            question: 'Different teams track different KPIs (engagement, revenue, feature adoption). Growth discussions feel fragmented. What do you do?',
            helperText: 'Tests strategic thinking and alignment',
            isScenario: true,
            options: [
              { value: 'standardize-reporting', label: 'Standardize reporting format', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'north-star-metric', label: 'Define a single north-star metric tied to long-term value', icon: <Target size={24} weight="duotone" /> },
              { value: 'teams-optimize', label: 'Let teams optimize independently', icon: <Users size={24} weight="duotone" /> },
              { value: 'add-dashboards', label: 'Add more dashboards', icon: <PresentationChart size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-m4',
            question: 'CEO wants to announce an AI feature at upcoming event. No validated use-case yet. You:',
            helperText: 'Tests AI literacy and strategic pushback',
            isScenario: true,
            options: [
              { value: 'build-wrapper', label: 'Build a lightweight AI wrapper for optics', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'test-workflow', label: 'Identify 1 high-friction workflow and test AI value', icon: <Brain size={24} weight="duotone" /> },
              { value: 'announce-roadmap', label: 'Announce roadmap without building', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'push-back', label: 'Push back entirely', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: PM-M5, PM-M6
      {
        id: 'pm-screen-3',
        initialChatText: "Finally, let's assess your data-driven approach and strategic reflection.",
        questions: [
          {
            id: 'pm-m5',
            question: 'Your activation is strong, but 90-day retention is declining gradually. What is your first structured move?',
            helperText: 'Tests data-driven problem-solving',
            isScenario: true,
            options: [
              { value: 'add-engagement', label: 'Add new engagement features', icon: <Code size={24} weight="duotone" /> },
              { value: 'churned-analysis', label: 'Run churned-user interviews + behavioral cohort analysis', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'increase-push', label: 'Increase push notifications', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'offer-discounts', label: 'Offer discounts', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-m6',
            question: 'A major feature failed to move the primary metric despite positive internal reviews. Root cause most likely?',
            helperText: 'Tests product strategy and problem discovery',
            isScenario: true,
            options: [
              { value: 'weak-discovery', label: 'Weak problem discovery', icon: <Users size={24} weight="duotone" /> },
              { value: 'poor-marketing', label: 'Poor marketing push', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'engineering-delays', label: 'Engineering delays', icon: <Code size={24} weight="duotone" /> },
              { value: 'competitive-timing', label: 'Competitive timing', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level)
    '8+': [
      // Screen 1: PM-S1, PM-S2
      {
        id: 'pm-screen-1',
        initialChatText: "Let's dive into your portfolio strategy and capital allocation thinking.",
        questions: [
          {
            id: 'pm-s1',
            question: 'Board mandates 2x revenue in 12 months. You lead a multi-product portfolio. Your first action:',
            helperText: 'Tests product strategy and portfolio thinking',
            isScenario: true,
            options: [
              { value: 'increase-pricing', label: 'Increase pricing across products', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'identify-ltv-icp', label: 'Identify highest LTV ICP and re-focus portfolio', icon: <Target size={24} weight="duotone" /> },
              { value: 'launch-features', label: 'Launch 3 new features', icon: <Code size={24} weight="duotone" /> },
              { value: 'expand-marketing', label: 'Expand marketing budget', icon: <MegaphoneSimple size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-s2',
            question: 'One product has strong engagement but low margin and weak strategic alignment. You:',
            helperText: 'Tests capital allocation thinking',
            isScenario: true,
            options: [
              { value: 'continue-engagement', label: 'Continue due to engagement', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'improve-monetization', label: 'Improve monetization', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'reevaluate-fit', label: 'Re-evaluate strategic fit + opportunity cost', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'shut-down', label: 'Shut it down immediately', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: PM-S3, PM-S4
      {
        id: 'pm-screen-2',
        initialChatText: "Now let's explore your AI strategy and organizational leadership.",
        questions: [
          {
            id: 'pm-s3',
            question: 'Competitors are adding similar AI features rapidly. True defensibility comes from:',
            helperText: 'Tests AI literacy and strategic moat thinking',
            options: [
              { value: 'better-ux', label: 'Better UX', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'faster-releases', label: 'Faster releases', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'proprietary-data', label: 'Proprietary data feedback loops', icon: <Database size={24} weight="duotone" /> },
              { value: 'larger-model', label: 'Larger model size', icon: <Brain size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-s4',
            question: 'Product velocity slows as org grows from 20 to 120 people. Root strategic issue likely:',
            helperText: 'Tests leadership and organizational design',
            isScenario: true,
            options: [
              { value: 'more-meetings', label: 'More meetings', icon: <Users size={24} weight="duotone" /> },
              { value: 'lack-ownership', label: 'Lack of clear ownership structure', icon: <Target size={24} weight="duotone" /> },
              { value: 'slower-engineers', label: 'Slower engineers', icon: <Code size={24} weight="duotone" /> },
              { value: 'poor-tooling', label: 'Poor tooling', icon: <Gear size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: PM-S5, PM-S6
      {
        id: 'pm-screen-3',
        initialChatText: "Finally, let's assess your strategic thinking and failure reflection.",
        questions: [
          {
            id: 'pm-s5',
            question: 'Company can expand into adjacent market OR deepen core product. You choose based on:',
            helperText: 'Tests strategic thinking and competitive advantage',
            isScenario: true,
            options: [
              { value: 'tam-size', label: 'TAM size', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'core-competency', label: 'Core competency + execution advantage', icon: <Target size={24} weight="duotone" /> },
              { value: 'investor-pressure', label: 'Investor pressure', icon: <Users size={24} weight="duotone" /> },
              { value: 'trend-momentum', label: 'Trend momentum', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'pm-s6',
            question: 'In hindsight, your most expensive mistake would likely come from:',
            helperText: 'Tests strategic thinking and failure patterns',
            isScenario: true,
            options: [
              { value: 'wrong-leadership', label: 'Hiring wrong leadership', icon: <Users size={24} weight="duotone" /> },
              { value: 'market-timing', label: 'Misreading market timing', icon: <Clock size={24} weight="duotone" /> },
              { value: 'underinvest-data', label: 'Underinvesting in data infrastructure', icon: <Database size={24} weight="duotone" /> },
              { value: 'feature-overbuilding', label: 'Feature overbuilding', icon: <Code size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  },

  'finance': {
    // 0-3 years experience (Entry level)
    '0-3': [
      // Screen 1: FM-E1, FM-E2
      {
        id: 'finance-screen-1',
        initialChatText: "Let's dive into your financial analysis and business partnering approach.",
        questions: [
          {
            id: 'fm-e1',
            question: 'You created a revenue forecast for the quarter. Actual revenue came 18% lower than projected. Your manager asks what happened. What is your first step?',
            helperText: 'Tests financial modeling and analytical thinking',
            isScenario: true,
            options: [
              { value: 'assume-sales-underperformed', label: 'Assume sales underperformed and report that', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'adjust-forecast', label: 'Adjust next quarter\'s forecast without analysis', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'revisit-assumptions', label: 'Revisit assumptions, drivers, and variance breakdown (volume, price, churn)', icon: <Target size={24} weight="duotone" /> },
              { value: 'blame-market', label: 'Blame market conditions broadly', icon: <XCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-e2',
            question: 'Sales leadership pushes for higher targets, but your analysis shows limited capacity. How do you respond?',
            helperText: 'Tests business partnering and influence',
            isScenario: true,
            options: [
              { value: 'agree-maintain-alignment', label: 'Agree to maintain alignment', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'reject-proposal', label: 'Reject proposal immediately', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'share-model-scenarios', label: 'Share model assumptions and scenario analysis to align on realistic targets', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'escalate-cfo', label: 'Escalate to CFO', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: FM-E3, FM-E4
      {
        id: 'finance-screen-2',
        initialChatText: "Now let's explore your data integrity and AI adoption.",
        questions: [
          {
            id: 'fm-e3',
            question: 'You notice revenue numbers in dashboard don\'t match finance sheet. What do you do?',
            helperText: 'Tests data integrity and rigor',
            isScenario: true,
            options: [
              { value: 'ignore-differences', label: 'Ignore minor differences', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'update-sheet', label: 'Update your sheet to match dashboard', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'trace-reconcile', label: 'Trace source of discrepancy and reconcile data before reporting', icon: <Target size={24} weight="duotone" /> },
              { value: 'send-disclaimer', label: 'Send numbers with disclaimer', icon: <CheckCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-e4',
            question: 'You spend 6 hours weekly updating Excel reports manually. Best approach?',
            helperText: 'Tests AI literacy and automation thinking',
            isScenario: true,
            options: [
              { value: 'continue-manual', label: 'Continue manually to avoid risk', icon: <Gear size={24} weight="duotone" /> },
              { value: 'ask-intern', label: 'Ask intern to do it', icon: <Users size={24} weight="duotone" /> },
              { value: 'explore-automation', label: 'Explore automation using Excel macros / BI tools / AI', icon: <Brain size={24} weight="duotone" /> },
              { value: 'reduce-reporting', label: 'Reduce reporting depth', icon: <ChartBar size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: FM-E5, FM-E6
      {
        id: 'finance-screen-3',
        initialChatText: "Finally, let's assess your strategic thinking and leadership.",
        questions: [
          {
            id: 'fm-e5',
            question: 'Company needs to cut 10% cost. What is your approach?',
            helperText: 'Tests strategic thinking and cost analysis',
            isScenario: true,
            options: [
              { value: 'cut-equally', label: 'Cut equally across departments', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'cut-largest', label: 'Cut largest cost center first', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'analyze-roi-strategic', label: 'Analyze ROI, strategic importance, and variable vs fixed cost', icon: <Target size={24} weight="duotone" /> },
              { value: 'freeze-hiring', label: 'Freeze hiring only', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-e6',
            question: 'In a business review, teams disagree on spending allocation.',
            helperText: 'Tests leadership and business partnering',
            isScenario: true,
            options: [
              { value: 'stay-silent', label: 'Stay silent', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'support-finance', label: 'Support finance position only', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'facilitate-data-scenarios', label: 'Facilitate discussion using data-backed scenarios', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'defer-decision', label: 'Defer decision', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level)
    '3-8': [
      // Screen 1: FM-M1, FM-M2
      {
        id: 'finance-screen-1',
        initialChatText: "Let's assess your financial modeling and business partnering depth.",
        questions: [
          {
            id: 'fm-m1',
            question: 'You\'re asked to build a 3-year projection for a new business line. Best approach?',
            helperText: 'Tests financial modeling sophistication',
            isScenario: true,
            options: [
              { value: 'extend-growth', label: 'Extend current year numbers with growth %', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'copy-competitor', label: 'Copy competitor assumptions', icon: <Buildings size={24} weight="duotone" /> },
              { value: 'driver-based-scenarios', label: 'Build driver-based model (acquisition, pricing, churn, cost structure) with scenarios', icon: <Target size={24} weight="duotone" /> },
              { value: 'optimistic-only', label: 'Present optimistic case only', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-m2',
            question: 'Product wants to launch a feature with unclear monetization.',
            helperText: 'Tests business partnering and strategic thinking',
            isScenario: true,
            options: [
              { value: 'block-uncertainty', label: 'Block it due to uncertainty', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'approve-no-analysis', label: 'Approve without analysis', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'build-business-case', label: 'Build business case with cost assumptions, revenue scenarios, and risk analysis', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'delay-indefinitely', label: 'Delay decision indefinitely', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: FM-M3, FM-M4
      {
        id: 'finance-screen-2',
        initialChatText: "Now let's explore your forecasting approach and data governance.",
        questions: [
          {
            id: 'fm-m3',
            question: 'Market conditions are volatile. How do you forecast?',
            helperText: 'Tests strategic financial planning',
            isScenario: true,
            options: [
              { value: 'single-point', label: 'Single-point forecast', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'conservative-only', label: 'Conservative estimate only', icon: <Target size={24} weight="duotone" /> },
              { value: 'scenarios-sensitivity', label: 'Base, upside, and downside scenarios with sensitivity analysis', icon: <Path size={24} weight="duotone" /> },
              { value: 'wait-stability', label: 'Wait for stability', icon: <Clock size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-m4',
            question: 'Multiple teams input financial data. Best governance approach?',
            helperText: 'Tests data integrity and process design',
            isScenario: true,
            options: [
              { value: 'trust-submissions', label: 'Trust team submissions', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'audit-randomly', label: 'Audit randomly', icon: <Target size={24} weight="duotone" /> },
              { value: 'validation-reconciliation', label: 'Implement validation checks, reconciliation processes, and ownership clarity', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'centralize-manual', label: 'Centralize everything manually', icon: <Gear size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: FM-M5, FM-M6
      {
        id: 'finance-screen-3',
        initialChatText: "Finally, let's assess your AI adoption and executive influence.",
        questions: [
          {
            id: 'fm-m5',
            question: 'AI tools can automate variance analysis.',
            helperText: 'Tests AI literacy and validation thinking',
            isScenario: true,
            options: [
              { value: 'avoid-ai-risk', label: 'Avoid AI due to risk', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'use-without-validation', label: 'Use AI outputs without validation', icon: <Brain size={24} weight="duotone" /> },
              { value: 'ai-validate-outputs', label: 'Use AI to generate insights but validate outputs before decision-making', icon: <Target size={24} weight="duotone" /> },
              { value: 'replace-analysts', label: 'Replace analysts fully', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-m6',
            question: 'CEO wants aggressive expansion but cash runway is limited.',
            helperText: 'Tests leadership and executive business partnering',
            isScenario: true,
            options: [
              { value: 'approve-expansion', label: 'Approve expansion', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'oppose-no-data', label: 'Oppose without data', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'present-scenarios-risk', label: 'Present scenario models, risk exposure, and capital strategy options', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'stay-neutral', label: 'Stay neutral', icon: <ChartBar size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level)
    '8+': [
      // Screen 1: FM-S1, FM-S2
      {
        id: 'finance-screen-1',
        initialChatText: "Let's assess your strategic capital allocation and M&A thinking.",
        questions: [
          {
            id: 'fm-s1',
            question: 'You manage portfolio-level budgeting across divisions. What defines strong allocation?',
            helperText: 'Tests strategic thinking and capital allocation',
            isScenario: true,
            options: [
              { value: 'even-distribution', label: 'Even distribution', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'prioritize-largest', label: 'Prioritize largest teams', icon: <Users size={24} weight="duotone" /> },
              { value: 'allocate-roi-strategic', label: 'Allocate based on ROI, strategic positioning, and long-term value creation', icon: <Target size={24} weight="duotone" /> },
              { value: 'protect-historical', label: 'Protect historical budgets', icon: <CheckCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-s2',
            question: 'Company is considering acquiring a smaller competitor. What is your approach?',
            helperText: 'Tests financial modeling and strategic thinking',
            isScenario: true,
            options: [
              { value: 'focus-revenue-synergy', label: 'Focus only on revenue synergy', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'look-ebitda', label: 'Look only at EBITDA multiple', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'evaluate-comprehensive', label: 'Evaluate synergy, integration cost, cultural fit, risk, and long-term value', icon: <Target size={24} weight="duotone" /> },
              { value: 'follow-board', label: 'Follow board preference', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: FM-S3, FM-S4
      {
        id: 'finance-screen-2',
        initialChatText: "Now let's explore your finance transformation and board-level communication.",
        questions: [
          {
            id: 'fm-s3',
            question: 'Finance processes are manual and fragmented.',
            helperText: 'Tests leadership and AI literacy',
            isScenario: true,
            options: [
              { value: 'add-headcount', label: 'Add more headcount', icon: <Users size={24} weight="duotone" /> },
              { value: 'maintain-status', label: 'Maintain status quo', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'drive-automation-governance', label: 'Drive automation roadmap, data governance, and KPI standardization', icon: <Brain size={24} weight="duotone" /> },
              { value: 'outsource-everything', label: 'Outsource everything', icon: <Buildings size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-s4',
            question: 'Board challenges your forecast assumptions.',
            helperText: 'Tests executive leadership and strategic clarity',
            isScenario: true,
            options: [
              { value: 'defend-aggressively', label: 'Defend aggressively', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'revise-immediately', label: 'Revise immediately', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'walk-through-transparent', label: 'Walk through assumptions, sensitivities, and risk buffers transparently', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'defer-ceo', label: 'Defer to CEO', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: FM-S5, FM-S6
      {
        id: 'finance-screen-3',
        initialChatText: "Finally, let's assess your enterprise risk management and team development.",
        questions: [
          {
            id: 'fm-s5',
            question: 'Macroeconomic downturn impacts demand projections. Best response?',
            helperText: 'Tests strategic thinking and risk management',
            isScenario: true,
            options: [
              { value: 'freeze-investments', label: 'Freeze all investments', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'ignore-long-term', label: 'Ignore long-term vision', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'rebalance-strategic', label: 'Rebalance cost structure, preserve cash, and protect strategic bets', icon: <Target size={24} weight="duotone" /> },
              { value: 'reduce-analytics', label: 'Reduce analytics budget', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'fm-s6',
            question: 'Finance team is strong technically but weak in business influence.',
            helperText: 'Tests leadership and business partnering',
            isScenario: true,
            options: [
              { value: 'focus-technical', label: 'Focus only on technical upskilling', icon: <Gear size={24} weight="duotone" /> },
              { value: 'hire-new-team', label: 'Hire new team entirely', icon: <Users size={24} weight="duotone" /> },
              { value: 'develop-storytelling', label: 'Develop storytelling, business acumen, and cross-functional exposure', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'increase-compliance', label: 'Increase compliance controls', icon: <CheckCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  },

  // Sales, Marketing, Operations, and Founder roles fully implemented
  // Student and Other roles remain as placeholders (decision needed on approach)
  'sales': {
    // 0-3 years experience (Entry level)
    '0-3': [
      // Screen 1: Sales Questions 1-2
      {
        id: 'sales-screen-1',
        initialChatText: "Let's dive into your sales approach and revenue thinking.",
        questions: [
          {
            id: 'sm-e1',
            question: 'You notice that many deals in the CRM are marked as "Negotiation" for over 45 days with no activity logged. What is your best first step?',
            helperText: 'Tests revenue operations',
            isScenario: true,
            options: [
              { value: 'ignore-senior-reps', label: 'Ignore it since senior reps manage their own pipeline', icon: <Users size={24} weight="duotone" /> },
              { value: 'close-randomly', label: 'Close old deals randomly to clean dashboard', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'audit-standardize', label: 'Audit deal stages, validate data with reps, and standardize update process', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'excel-separate', label: 'Create a new Excel sheet and track separately', icon: <PresentationChart size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-e2',
            question: 'A high-value prospect has gone silent after pricing discussion. What do you do?',
            helperText: 'Tests deal execution',
            isScenario: true,
            options: [
              { value: 'wait-respond', label: 'Wait for them to respond', icon: <Clock size={24} weight="duotone" /> },
              { value: 'offer-discount', label: 'Offer a discount immediately', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'reengage-objections', label: 'Re-engage by understanding objections and reframing value', icon: <Target size={24} weight="duotone" /> },
              { value: 'escalate-ceo', label: 'Escalate to CEO immediately', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: Sales Questions 3-4
      {
        id: 'sales-screen-2',
        initialChatText: "Now let's explore your sales strategy and AI application.",
        questions: [
          {
            id: 'sm-e3',
            question: 'Your team missed monthly revenue target by 12%. What is the most structured response?',
            helperText: 'Tests sales strategy',
            isScenario: true,
            options: [
              { value: 'push-harder', label: 'Push team to work harder next month', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'blame-marketing', label: 'Blame marketing for weak leads', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'analyze-funnel', label: 'Analyze funnel metrics (lead quality, conversion rates, deal velocity)', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'increase-discounts', label: 'Increase discounts across the board', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-e4',
            question: 'You spend hours preparing weekly pipeline reports manually. Best approach?',
            helperText: 'Tests AI literacy',
            isScenario: true,
            options: [
              { value: 'continue-manual', label: 'Continue manual reporting', icon: <Clock size={24} weight="duotone" /> },
              { value: 'ask-admin', label: 'Ask admin team to do it', icon: <Users size={24} weight="duotone" /> },
              { value: 'explore-automation', label: 'Explore CRM automation, dashboards, or AI reporting tools', icon: <Brain size={24} weight="duotone" /> },
              { value: 'reduce-detail', label: 'Reduce reporting detail', icon: <PresentationChart size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: Sales Questions 5-6
      {
        id: 'sales-screen-3',
        initialChatText: "Finally, let's assess your strategic thinking and leadership.",
        questions: [
          {
            id: 'sm-e5',
            question: 'Multiple prospects say pricing is too high.',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'offer-discount-immediate', label: 'Offer immediate 20% discount', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'assume-product', label: 'Assume product isn\'t good enough', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'investigate-value', label: 'Investigate value perception, competitor positioning, and ICP fit', icon: <Target size={24} weight="duotone" /> },
              { value: 'reduce-base-pricing', label: 'Reduce base pricing for everyone', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-e6',
            question: 'Marketing blames Sales for low conversion. Sales blames Marketing for poor leads.',
            helperText: 'Tests leadership & revenue ops',
            isScenario: true,
            options: [
              { value: 'stay-out', label: 'Stay out of it', icon: <Users size={24} weight="duotone" /> },
              { value: 'support-sales', label: 'Support Sales team only', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'align-data', label: 'Align both teams using data (MQL to SQL conversion, lead quality analysis)', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'escalate-conflict', label: 'Escalate conflict', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level)
    '3-8': [
      // Screen 1: Sales Questions 1-2
      {
        id: 'sales-screen-1',
        initialChatText: "Let's dive into your sales approach and revenue thinking.",
        questions: [
          {
            id: 'sm-m1',
            question: 'Your revenue is growing but unpredictable month to month. What is your approach?',
            helperText: 'Tests revenue operations',
            isScenario: true,
            options: [
              { value: 'increase-targets', label: 'Increase targets', icon: <Target size={24} weight="duotone" /> },
              { value: 'hire-more-reps', label: 'Hire more reps immediately', icon: <Users size={24} weight="duotone" /> },
              { value: 'strengthen-forecasting', label: 'Strengthen forecasting model, pipeline hygiene, and stage conversion analysis', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'focus-quarter-end', label: 'Focus only on closing quarter-end deals', icon: <Clock size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-m2',
            question: 'An enterprise client requires custom pricing, integrations, and long procurement cycles. What do you prioritize?',
            helperText: 'Tests deal execution',
            isScenario: true,
            options: [
              { value: 'offer-max-discount', label: 'Offer maximum discount to close quickly', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'agree-all-customizations', label: 'Agree to all customizations', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'map-stakeholders', label: 'Map stakeholders, decision process, value justification, and risk mitigation', icon: <Target size={24} weight="duotone" /> },
              { value: 'avoid-complex', label: 'Avoid complex deals', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: Sales Questions 3-4
      {
        id: 'sales-screen-2',
        initialChatText: "Now let's explore your sales strategy and AI application.",
        questions: [
          {
            id: 'sm-m3',
            question: 'Leadership wants to expand into mid-market from SMB. What is your first step?',
            helperText: 'Tests sales strategy',
            isScenario: true,
            options: [
              { value: 'replicate-pitch', label: 'Replicate same sales pitch', icon: <PresentationChart size={24} weight="duotone" /> },
              { value: 'increase-ad-budget', label: 'Increase ad budget', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'define-icp', label: 'Define ICP, pricing adjustments, sales cycle expectations, and enablement needs', icon: <Target size={24} weight="duotone" /> },
              { value: 'assign-existing-reps', label: 'Assign existing reps without change', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-m4',
            question: 'AI tools can automate lead scoring and outreach personalization.',
            helperText: 'Tests AI literacy',
            isScenario: true,
            options: [
              { value: 'avoid-ai-fear', label: 'Avoid AI due to fear of errors', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'fully-automate', label: 'Fully automate without oversight', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'use-ai-validate', label: 'Use AI for prospect prioritization while validating outputs', icon: <Brain size={24} weight="duotone" /> },
              { value: 'continue-manual-outreach', label: 'Continue manual outreach', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: Sales Questions 5-6
      {
        id: 'sales-screen-3',
        initialChatText: "Finally, let's assess your strategic thinking and leadership.",
        questions: [
          {
            id: 'sm-m5',
            question: 'Win rate dropped from 30% to 18%. Your structured action?',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'increase-discounts', label: 'Increase discounts', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'replace-reps', label: 'Replace lowest-performing reps', icon: <Users size={24} weight="duotone" /> },
              { value: 'analyze-loss-reasons', label: 'Analyze deal loss reasons, competitor trends, and sales messaging gaps', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'ignore-temporarily', label: 'Ignore temporarily', icon: <Clock size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-m6',
            question: 'Two high performers dominate revenue; rest lag significantly.',
            helperText: 'Tests leadership',
            isScenario: true,
            options: [
              { value: 'focus-top-performers', label: 'Focus only on top performers', icon: <Trophy size={24} weight="duotone" /> },
              { value: 'let-underperformers', label: 'Let underperformers figure it out', icon: <Users size={24} weight="duotone" /> },
              { value: 'standardize-playbooks', label: 'Standardize playbooks, coaching, and performance benchmarks', icon: <Target size={24} weight="duotone" /> },
              { value: 'reduce-targets', label: 'Reduce targets', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level)
    '8+': [
      // Screen 1: Sales Questions 1-2
      {
        id: 'sales-screen-1',
        initialChatText: "Let's dive into your sales approach and revenue thinking.",
        questions: [
          {
            id: 'sm-s1',
            question: 'Company growth has plateaued. What defines a strong revenue strategy?',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'increase-targets-aggressive', label: 'Increase targets aggressively', icon: <Target size={24} weight="duotone" /> },
              { value: 'increase-discounting', label: 'Increase discounting', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'reevaluate-strategy', label: 'Re-evaluate ICP, pricing strategy, channel mix, and product-market fit', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'replace-team', label: 'Replace entire sales team', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-s2',
            question: 'You oversee multiple revenue channels (Inbound, Outbound, Partnerships). How do you allocate budget?',
            helperText: 'Tests revenue operations & strategy',
            isScenario: true,
            options: [
              { value: 'equal-distribution', label: 'Equal distribution', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'favor-dominant', label: 'Favor historically dominant channel', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'allocate-cac-ltv', label: 'Allocate based on CAC, LTV, scalability, and margin impact', icon: <Target size={24} weight="duotone" /> },
              { value: 'follow-last-year', label: 'Follow last year\'s budget', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: Sales Questions 3-4
      {
        id: 'sales-screen-2',
        initialChatText: "Now let's explore your strategic thinking and AI application.",
        questions: [
          {
            id: 'sm-s3',
            question: '40% of revenue comes from 3 large clients.',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'continue-focus', label: 'Continue focus on them', icon: <Target size={24} weight="duotone" /> },
              { value: 'increase-dependency', label: 'Increase dependency', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'diversify-revenue', label: 'Diversify revenue base and reduce concentration risk', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'ignore-risk', label: 'Ignore risk', icon: <XCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-s4',
            question: 'Board wants AI-driven revenue transformation.',
            helperText: 'Tests AI literacy',
            isScenario: true,
            options: [
              { value: 'buy-expensive-tool', label: 'Buy expensive AI tool immediately', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'resist-transformation', label: 'Resist transformation', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'define-use-cases', label: 'Define use cases (forecasting, scoring, automation), ROI, and phased rollout', icon: <Brain size={24} weight="duotone" /> },
              { value: 'fully-automate-sales', label: 'Fully automate sales', icon: <Gear size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: Sales Questions 5-6
      {
        id: 'sales-screen-3',
        initialChatText: "Finally, let's assess your leadership and organizational thinking.",
        questions: [
          {
            id: 'sm-s5',
            question: 'Board challenges your forecast accuracy.',
            helperText: 'Tests leadership & revenue ops',
            isScenario: true,
            options: [
              { value: 'defend-emotionally', label: 'Defend emotionally', icon: <Users size={24} weight="duotone" /> },
              { value: 'revise-numbers', label: 'Revise numbers quickly', icon: <Clock size={24} weight="duotone" /> },
              { value: 'walk-through-assumptions', label: 'Walk through assumptions, pipeline health, sensitivity scenarios', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'defer-finance', label: 'Defer to finance', icon: <PresentationChart size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sm-s6',
            question: 'Revenue team grew from 20 to 80 members. What is critical?',
            helperText: 'Tests leadership',
            isScenario: true,
            options: [
              { value: 'more-pressure', label: 'More pressure on targets', icon: <Target size={24} weight="duotone" /> },
              { value: 'increase-commissions', label: 'Increase commissions only', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'clear-segmentation', label: 'Clear segmentation, enablement structure, performance systems, and culture alignment', icon: <Users size={24} weight="duotone" /> },
              { value: 'reduce-reporting', label: 'Reduce reporting', icon: <PresentationChart size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  },

  'marketing': {
    // 0-3 years experience (Entry level)
    '0-3': [
      // Screen 1: MM-E1, MM-E2
      {
        id: 'marketing-screen-1',
        initialChatText: "Let's explore your marketing analytics and campaign optimization approach.",
        questions: [
          {
            id: 'mm-e1',
            question: "You're running paid ads for lead generation. Over the last 2 weeks, Cost per Acquisition (CAC) has increased by 30%, while impressions remain stable. What is your first step?",
            helperText: 'Tests Marketing Analytics',
            isScenario: true,
            options: [
              { value: 'increase-budget', label: 'Increase budget to regain volume', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'pause-campaigns', label: 'Pause all campaigns immediately', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'analyze-funnel-metrics', label: 'Analyze funnel metrics (CTR, CPC, conversion rate, audience segmentation)', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'change-creatives-randomly', label: 'Change ad creatives randomly', icon: <Lightning size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-e2',
            question: 'Your campaign drives traffic successfully, but landing page conversion rate is low (2%). What do you do?',
            helperText: 'Tests Campaign Optimization',
            isScenario: true,
            options: [
              { value: 'increase-traffic', label: 'Increase traffic volume', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'assume-audience-wrong', label: 'Assume audience is wrong', icon: <Users size={24} weight="duotone" /> },
              { value: 'run-ab-tests', label: 'Run A/B tests on messaging, CTA, and page layout', icon: <Target size={24} weight="duotone" /> },
              { value: 'add-more-content', label: 'Add more content to the page', icon: <PresentationChart size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: MM-E3, MM-E4
      {
        id: 'marketing-screen-2',
        initialChatText: "Now let's assess your growth marketing strategy and AI adoption.",
        questions: [
          {
            id: 'mm-e3',
            question: "You're launching a new AI Resume Builder feature. How do you start?",
            helperText: 'Tests Growth Marketing',
            isScenario: true,
            options: [
              { value: 'launch-ads-immediately', label: 'Launch ads immediately', icon: <Rocket size={24} weight="duotone" /> },
              { value: 'copy-competitor-messaging', label: 'Copy competitor messaging', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'define-persona-strategy', label: 'Define target persona, value proposition, channels, and success metrics', icon: <Target size={24} weight="duotone" /> },
              { value: 'increase-brand-posts', label: 'Increase brand posts on social media', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-e4',
            question: 'You manually create weekly campaign reports using spreadsheets. Best approach?',
            helperText: 'Tests AI Literacy',
            isScenario: true,
            options: [
              { value: 'continue-manually', label: 'Continue manually to avoid errors', icon: <Clock size={24} weight="duotone" /> },
              { value: 'reduce-frequency', label: 'Reduce report frequency', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'use-automation-ai', label: 'Use automation tools, dashboards, or AI to streamline reporting', icon: <Brain size={24} weight="duotone" /> },
              { value: 'delegate-without-structure', label: 'Delegate without structure', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: MM-E5, MM-E6
      {
        id: 'marketing-screen-3',
        initialChatText: "Finally, let's understand your strategic thinking and leadership approach.",
        questions: [
          {
            id: 'mm-e5',
            question: 'Brand team wants long-term awareness campaigns. Performance team wants short-term ROI campaigns.',
            helperText: 'Tests Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'support-performance-only', label: 'Support performance only', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'support-brand-only', label: 'Support brand only', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'propose-balanced-mix', label: 'Propose a balanced mix aligned with business goals', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'escalate-conflict', label: 'Escalate conflict', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-e6',
            question: 'Sales says marketing leads are poor quality.',
            helperText: 'Tests Leadership & Analytics',
            isScenario: true,
            options: [
              { value: 'defend-marketing', label: 'Defend marketing team', icon: <Users size={24} weight="duotone" /> },
              { value: 'ignore-feedback', label: 'Ignore feedback', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'analyze-mql-sql', label: 'Analyze MQL → SQL conversion and refine targeting', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'reduce-lead-generation', label: 'Reduce lead generation efforts', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level)
    '3-8': [
      // Screen 1: MM-M1, MM-M2
      {
        id: 'marketing-screen-1',
        initialChatText: "Let's explore your growth marketing and analytics capabilities.",
        questions: [
          {
            id: 'mm-m1',
            question: 'Your campaigns are delivering growth, but CAC is rising gradually each quarter. What is your structured approach?',
            helperText: 'Tests Growth Marketing & Campaign Optimization',
            isScenario: true,
            options: [
              { value: 'increase-budgets', label: 'Increase budgets to maintain growth', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'cut-brand-spend', label: 'Cut brand spend entirely', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'optimize-channel-mix', label: 'Optimize channel mix, creative testing, audience segmentation, and LTV analysis', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'accept-higher-cac', label: 'Accept higher CAC', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-m2',
            question: 'Attribution data across Meta, Google, and CRM does not match.',
            helperText: 'Tests Marketing Analytics',
            isScenario: true,
            options: [
              { value: 'trust-last-click', label: 'Trust last-click attribution blindly', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'choose-best-numbers', label: 'Choose platform with best numbers', icon: <ChartLineUp size={24} weight="duotone" /> },
              { value: 'investigate-reconcile', label: 'Investigate attribution model and reconcile cross-channel data', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'report-highest-number', label: 'Report highest number', icon: <Target size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: MM-M3, MM-M4
      {
        id: 'marketing-screen-2',
        initialChatText: "Now let's assess your strategic thinking and AI application.",
        questions: [
          {
            id: 'mm-m3',
            question: 'Company wants to target working professionals instead of students. What is your first move?',
            helperText: 'Tests Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'use-same-messaging', label: 'Use same messaging', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'increase-influencer', label: 'Increase influencer partnerships', icon: <Users size={24} weight="duotone" /> },
              { value: 'conduct-market-research', label: 'Conduct market research and reposition value proposition', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'reduce-budget', label: 'Reduce budget', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-m4',
            question: 'AI tools can generate multiple ad creatives automatically.',
            helperText: 'Tests AI Literacy',
            isScenario: true,
            options: [
              { value: 'use-ai-without-testing', label: 'Use AI creatives without testing', icon: <Brain size={24} weight="duotone" /> },
              { value: 'avoid-ai-tools', label: 'Avoid AI tools', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'use-ai-validate-testing', label: 'Use AI for ideation but validate through structured A/B testing', icon: <Target size={24} weight="duotone" /> },
              { value: 'replace-creative-team', label: 'Replace creative team', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: MM-M5, MM-M6
      {
        id: 'marketing-screen-3',
        initialChatText: "Finally, let's understand your campaign optimization and leadership approach.",
        questions: [
          {
            id: 'mm-m5',
            question: 'A flagship campaign underperformed despite high spend. What do you do?',
            helperText: 'Tests Campaign Optimization',
            isScenario: true,
            options: [
              { value: 'reduce-marketing-budget', label: 'Reduce marketing budget', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'blame-external-factors', label: 'Blame external factors', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'analyze-audience-targeting', label: 'Analyze audience targeting, messaging alignment, and funnel conversion', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'stop-experimenting', label: 'Stop experimenting', icon: <CheckCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-m6',
            question: 'Your team has strong execution but lacks strategic alignment.',
            helperText: 'Tests Leadership',
            isScenario: true,
            options: [
              { value: 'push-higher-targets', label: 'Push for higher targets', icon: <Target size={24} weight="duotone" /> },
              { value: 'focus-only-reporting', label: 'Focus only on reporting', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'align-team-objectives', label: 'Align team with clear growth objectives and performance benchmarks', icon: <Users size={24} weight="duotone" /> },
              { value: 'micromanage-campaigns', label: 'Micromanage campaigns', icon: <Gear size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level)
    '8+': [
      // Screen 1: MM-S1, MM-S2
      {
        id: 'marketing-screen-1',
        initialChatText: "Let's explore your strategic marketing and portfolio allocation thinking.",
        questions: [
          {
            id: 'mm-s1',
            question: 'Growth has slowed. Short-term performance marketing is saturated. Your approach?',
            helperText: 'Tests Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'increase-discounts', label: 'Increase discounts', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'double-performance-budget', label: 'Double performance budget', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'strengthen-brand-equity', label: 'Strengthen brand equity while optimizing performance efficiency', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'reduce-marketing-investment', label: 'Reduce marketing investment', icon: <XCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-s2',
            question: 'You oversee Paid, Organic, Influencer, and Partnerships channels. How do you allocate budget?',
            helperText: 'Tests Growth Marketing & Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'equal-distribution', label: 'Equal distribution', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'allocate-last-year', label: "Allocate based on last year's budget", icon: <Clock size={24} weight="duotone" /> },
              { value: 'allocate-cac-ltv-roi', label: 'Allocate based on CAC, LTV, scalability, and marginal ROI', icon: <Target size={24} weight="duotone" /> },
              { value: 'focus-one-channel', label: 'Focus only on one channel', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: MM-S3, MM-S4
      {
        id: 'marketing-screen-2',
        initialChatText: "Now let's assess your analytics leadership and AI transformation strategy.",
        questions: [
          {
            id: 'mm-s3',
            question: 'Board questions marketing ROI accuracy.',
            helperText: 'Tests Marketing Analytics',
            isScenario: true,
            options: [
              { value: 'defend-current-model', label: 'Defend current model', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'simplify-reporting', label: 'Simplify reporting', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'redesign-attribution-framework', label: 'Redesign attribution framework integrating CRM, cohort, and incrementality analysis', icon: <Target size={24} weight="duotone" /> },
              { value: 'reduce-reporting-depth', label: 'Reduce reporting depth', icon: <XCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-s4',
            question: 'CEO wants AI-driven personalization at scale.',
            helperText: 'Tests AI Literacy & Leadership',
            isScenario: true,
            options: [
              { value: 'buy-expensive-tool', label: 'Buy expensive tool immediately', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'resist-change', label: 'Resist change', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'identify-pilot-scale', label: 'Identify high-impact use cases, pilot, measure ROI, then scale', icon: <Brain size={24} weight="duotone" /> },
              { value: 'fully-automate-campaigns', label: 'Fully automate campaigns', icon: <Gear size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: MM-S5, MM-S6
      {
        id: 'marketing-screen-3',
        initialChatText: "Finally, let's understand your strategic expansion and organizational leadership.",
        questions: [
          {
            id: 'mm-s5',
            question: "You're expanding into a new region with different cultural context. What is critical?",
            helperText: 'Tests Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'replicate-same-campaigns', label: 'Replicate same campaigns', icon: <MegaphoneSimple size={24} weight="duotone" /> },
              { value: 'increase-paid-ads', label: 'Increase paid ads', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'localize-strategy', label: 'Localize messaging, pricing, and channel strategy', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'focus-only-digital', label: 'Focus only on digital', icon: <Target size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'mm-s6',
            question: 'Marketing team grew from 15 to 60 members. What matters most now?',
            helperText: 'Tests Leadership',
            isScenario: true,
            options: [
              { value: 'increase-budget-only', label: 'Increase budget only', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'focus-only-acquisition', label: 'Focus only on acquisition', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'define-specialization-kpis', label: 'Define specialization, KPIs, experimentation culture, and leadership structure', icon: <Users size={24} weight="duotone" /> },
              { value: 'reduce-reporting', label: 'Reduce reporting', icon: <ChartBar size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  },

  'operations': {
    // 0-3 years experience (Entry level)
    '0-3': [
      // Screen 1: OM-E1, OM-E2
      {
        id: 'operations-screen-1',
        initialChatText: "Let's explore your operations thinking and problem-solving approach.",
        questions: [
          {
            id: 'om-e1',
            question: 'Customer complaints about late deliveries have increased by 25% in the last month. There has been no major demand spike. What is your first step?',
            helperText: 'Tests Operations Excellence',
            isScenario: true,
            options: [
              { value: 'increase-delivery-timelines', label: 'Increase delivery timelines on the website', icon: <Clock size={24} weight="duotone" /> },
              { value: 'blame-warehouse-staff', label: 'Blame warehouse staff', icon: <Users size={24} weight="duotone" /> },
              { value: 'analyze-fulfillment-cycle', label: 'Analyze fulfillment cycle time, bottlenecks, and error rates', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'offer-discounts', label: 'Offer discounts to affected customers', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-e2',
            question: 'System shows 1,200 units in stock, but physical count shows 1,050 units. What do you do?',
            helperText: 'Tests Supply Chain & Data Integrity',
            isScenario: true,
            options: [
              { value: 'adjust-system-match', label: 'Adjust system to match physical count', icon: <Database size={24} weight="duotone" /> },
              { value: 'ignore-small-discrepancy', label: 'Ignore small discrepancy', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'investigate-root-cause', label: 'Investigate root cause (process gap, shrinkage, system error) before updating', icon: <Target size={24} weight="duotone" /> },
              { value: 'order-more-inventory', label: 'Order more inventory immediately', icon: <ShoppingCart size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: OM-E3, OM-E4
      {
        id: 'operations-screen-2',
        initialChatText: "Now let's assess your process automation and supply chain management.",
        questions: [
          {
            id: 'om-e3',
            question: 'You spend hours manually consolidating data from multiple spreadsheets weekly. Best approach?',
            helperText: 'Tests Process Automation',
            isScenario: true,
            options: [
              { value: 'continue-manually', label: 'Continue manually to avoid risk', icon: <Gear size={24} weight="duotone" /> },
              { value: 'reduce-reporting-scope', label: 'Reduce reporting scope', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'explore-automation', label: 'Explore automation using BI tools or scripts', icon: <Brain size={24} weight="duotone" /> },
              { value: 'delegate-without-docs', label: 'Delegate without documentation', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-e4',
            question: 'A key supplier frequently delays shipments, impacting operations.',
            helperText: 'Tests Supply Chain Management',
            isScenario: true,
            options: [
              { value: 'terminate-contract', label: 'Immediately terminate contract', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'ignore-delays', label: 'Ignore delays', icon: <Clock size={24} weight="duotone" /> },
              { value: 'review-slas-discuss', label: 'Review SLAs, discuss root causes, and evaluate alternate suppliers', icon: <Target size={24} weight="duotone" /> },
              { value: 'increase-buffer-inventory', label: 'Increase buffer inventory without analysis', icon: <ShoppingCart size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: OM-E5, OM-E6
      {
        id: 'operations-screen-3',
        initialChatText: "Finally, let's understand your AI literacy and leadership approach.",
        questions: [
          {
            id: 'om-e5',
            question: 'AI tools can forecast demand more accurately than manual estimation.',
            helperText: 'Tests AI Literacy',
            isScenario: true,
            options: [
              { value: 'avoid-using-ai', label: 'Avoid using AI', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'replace-planning-team', label: 'Replace planning team entirely', icon: <Users size={24} weight="duotone" /> },
              { value: 'pilot-ai-forecasting', label: 'Pilot AI forecasting while validating against historical data', icon: <Brain size={24} weight="duotone" /> },
              { value: 'use-ai-without-verification', label: 'Use AI outputs without verification', icon: <Lightning size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-e6',
            question: 'Operations blames procurement for delays; procurement blames operations.',
            helperText: 'Tests Leadership & Operations Excellence',
            isScenario: true,
            options: [
              { value: 'stay-out-conflict', label: 'Stay out of conflict', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'support-own-team', label: 'Support your own team', icon: <Users size={24} weight="duotone" /> },
              { value: 'map-end-to-end', label: 'Map end-to-end process and identify breakdown points collaboratively', icon: <Path size={24} weight="duotone" /> },
              { value: 'escalate-immediately', label: 'Escalate immediately', icon: <Target size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level)
    '3-8': [
      // Screen 1: OM-M1, OM-M2
      {
        id: 'operations-screen-1',
        initialChatText: "Let's explore your operations thinking and strategic problem-solving approach.",
        questions: [
          {
            id: 'om-m1',
            question: 'Operational costs have increased by 18% year-over-year without proportional revenue growth. What is your structured response?',
            helperText: 'Tests Operations Excellence',
            isScenario: true,
            options: [
              { value: 'cut-costs-equally', label: 'Cut costs across all departments equally', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'reduce-workforce', label: 'Reduce workforce', icon: <Users size={24} weight="duotone" /> },
              { value: 'conduct-cost-driver-analysis', label: 'Conduct cost-driver analysis and optimize high-impact inefficiencies', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'increase-pricing', label: 'Increase pricing', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-m2',
            question: 'A major supplier shuts down unexpectedly. Your first strategic move?',
            helperText: 'Tests Supply Chain Strategy',
            isScenario: true,
            options: [
              { value: 'pause-operations', label: 'Pause operations', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'increase-customer-prices', label: 'Increase customer prices immediately', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'activate-alternate-suppliers', label: 'Activate alternate suppliers and assess risk diversification', icon: <Buildings size={24} weight="duotone" /> },
              { value: 'wait-resolution', label: 'Wait for resolution', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: OM-M3, OM-M4
      {
        id: 'operations-screen-2',
        initialChatText: "Now let's assess your scaling strategy and automation thinking.",
        questions: [
          {
            id: 'om-m3',
            question: 'Demand has doubled in 12 months. Current processes are breaking. What is critical?',
            helperText: 'Tests Strategic Thinking & Operations Excellence',
            isScenario: true,
            options: [
              { value: 'increase-overtime', label: 'Increase overtime hours', icon: <Clock size={24} weight="duotone" /> },
              { value: 'hire-aggressively', label: 'Hire aggressively without structure', icon: <Users size={24} weight="duotone" /> },
              { value: 'redesign-workflows', label: 'Redesign workflows, capacity planning, and process automation', icon: <Gear size={24} weight="duotone" /> },
              { value: 'slow-down-growth', label: 'Slow down growth', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-m4',
            question: 'A manual approval process causes 2-day delays.',
            helperText: 'Tests AI Literacy',
            isScenario: true,
            options: [
              { value: 'add-more-approvers', label: 'Add more approvers', icon: <Users size={24} weight="duotone" /> },
              { value: 'remove-approvals', label: 'Remove approvals entirely', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'evaluate-workflow-automation', label: 'Evaluate workflow automation with controls and audit trail', icon: <Brain size={24} weight="duotone" /> },
              { value: 'ignore-issue', label: 'Ignore issue', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: OM-M5, OM-M6
      {
        id: 'operations-screen-3',
        initialChatText: "Finally, let's understand your data governance and leadership approach.",
        questions: [
          {
            id: 'om-m5',
            question: 'Multiple dashboards show conflicting performance metrics.',
            helperText: 'Tests Operations Excellence & Data Governance',
            isScenario: true,
            options: [
              { value: 'choose-favorable-metric', label: 'Choose the most favorable metric', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'report-average', label: 'Report average', icon: <Target size={24} weight="duotone" /> },
              { value: 'standardize-kpi-definitions', label: 'Standardize KPI definitions and reconcile data sources', icon: <Database size={24} weight="duotone" /> },
              { value: 'reduce-reporting', label: 'Reduce reporting', icon: <XCircle size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-m6',
            question: 'Product launches often face operational readiness issues.',
            helperText: 'Tests Leadership',
            isScenario: true,
            options: [
              { value: 'let-teams-manage', label: 'Let teams manage independently', icon: <Users size={24} weight="duotone" /> },
              { value: 'increase-pressure', label: 'Increase pressure on operations', icon: <Target size={24} weight="duotone" /> },
              { value: 'implement-launch-checklists', label: 'Implement launch checklists, accountability owners, and milestone tracking', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'delay-launches', label: 'Delay launches', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level)
    '8+': [
      // Screen 1: OM-S1, OM-S2
      {
        id: 'operations-screen-1',
        initialChatText: "Let's explore your strategic operations thinking and long-term planning.",
        questions: [
          {
            id: 'om-s1',
            question: 'Company growth has plateaued due to operational constraints. What defines strong operational strategy?',
            helperText: 'Tests Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'increase-headcount', label: 'Increase headcount', icon: <Users size={24} weight="duotone" /> },
              { value: 'maintain-current-structure', label: 'Maintain current structure', icon: <Gear size={24} weight="duotone" /> },
              { value: 'redesign-operating-model', label: 'Redesign operating model aligned with long-term growth goals', icon: <Target size={24} weight="duotone" /> },
              { value: 'cut-costs-aggressively', label: 'Cut costs aggressively', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-s2',
            question: '60% of raw materials come from one geographic region.',
            helperText: 'Tests Supply Chain Strategy',
            isScenario: true,
            options: [
              { value: 'continue-as-is', label: 'Continue as is', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'increase-dependency', label: 'Increase dependency', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'diversify-supplier-base', label: 'Diversify supplier base and build contingency plans', icon: <Buildings size={24} weight="duotone" /> },
              { value: 'increase-inventory-buffer', label: 'Increase inventory buffer only', icon: <ShoppingCart size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: OM-S3, OM-S4
      {
        id: 'operations-screen-2',
        initialChatText: "Now let's assess your automation strategy and board-level leadership.",
        questions: [
          {
            id: 'om-s3',
            question: 'Board wants digital transformation across operations. Your approach?',
            helperText: 'Tests AI Literacy & Process Automation',
            isScenario: true,
            options: [
              { value: 'buy-multiple-tools', label: 'Buy multiple tools immediately', icon: <Gear size={24} weight="duotone" /> },
              { value: 'resist-change', label: 'Resist change', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'define-phased-automation', label: 'Define phased automation roadmap with ROI tracking', icon: <Brain size={24} weight="duotone" /> },
              { value: 'fully-automate-without-pilots', label: 'Fully automate without pilots', icon: <Lightning size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-s4',
            question: 'Board questions declining operational efficiency metrics.',
            helperText: 'Tests Leadership',
            isScenario: true,
            options: [
              { value: 'defend-current-processes', label: 'Defend current processes', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'revise-metrics-presentation', label: 'Revise metrics presentation', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'present-root-cause-analysis', label: 'Present root-cause analysis, benchmark data, and improvement plan', icon: <Target size={24} weight="duotone" /> },
              { value: 'defer-to-ceo', label: 'Defer to CEO', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: OM-S5, OM-S6
      {
        id: 'operations-screen-3',
        initialChatText: "Finally, let's understand your expansion strategy and team building approach.",
        questions: [
          {
            id: 'om-s5',
            question: 'Company plans to expand internationally. Operationally, what matters most?',
            helperText: 'Tests Strategic Thinking',
            isScenario: true,
            options: [
              { value: 'replicate-domestic-model', label: 'Replicate domestic model', icon: <Gear size={24} weight="duotone" /> },
              { value: 'focus-only-logistics', label: 'Focus only on logistics', icon: <ShoppingCart size={24} weight="duotone" /> },
              { value: 'assess-regulatory-supplier', label: 'Assess regulatory, supplier ecosystem, cost structure, and scalability', icon: <Target size={24} weight="duotone" /> },
              { value: 'increase-pricing', label: 'Increase pricing', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'om-s6',
            question: 'Operations team grew from 25 to 100 people. What is critical?',
            helperText: 'Tests Leadership & Operations Excellence',
            isScenario: true,
            options: [
              { value: 'increase-monitoring-only', label: 'Increase monitoring only', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'focus-only-daily-execution', label: 'Focus only on daily execution', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'establish-clear-ownership', label: 'Establish clear ownership, performance metrics, and continuous improvement culture', icon: <Users size={24} weight="duotone" /> },
              { value: 'reduce-documentation', label: 'Reduce documentation', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  },

  'founder': {
    // 0-3 years experience (Entry level)
    '0-3': [
      // Screen 1: SF-E1, SF-E2
      {
        id: 'founder-screen-1',
        initialChatText: "Let's explore your founder mindset and strategic approach.",
        questions: [
          {
            id: 'sf-e1',
            question: 'You have an idea for an AI-powered career guidance app. You\'re excited and ready to build the MVP. What should you do first?',
            helperText: 'Tests venture building',
            isScenario: true,
            options: [
              { value: 'hire-developers', label: 'Hire developers immediately', icon: <Code size={24} weight="duotone" /> },
              { value: 'build-full-product', label: 'Build a full-featured product before launch', icon: <Gear size={24} weight="duotone" /> },
              { value: 'validate-demand', label: 'Validate demand through user interviews, landing pages, or small experiments', icon: <Users size={24} weight="duotone" /> },
              { value: 'raise-funding', label: 'Raise funding first', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-e2',
            question: 'After launch, you get sign-ups but very few users are willing to pay. What is your most structured response?',
            helperText: 'Tests business fundamentals',
            isScenario: true,
            options: [
              { value: 'reduce-pricing', label: 'Reduce pricing drastically', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'assume-no-value', label: 'Assume users don\'t value the product', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'reassess-value-prop', label: 'Reassess value proposition, ICP fit, and pricing strategy', icon: <Brain size={24} weight="duotone" /> },
              { value: 'increase-marketing', label: 'Increase marketing spend', icon: <MegaphoneSimple size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: SF-E3, SF-E4
      {
        id: 'founder-screen-2',
        initialChatText: "Now let's assess your strategic thinking and resourcefulness.",
        questions: [
          {
            id: 'sf-e3',
            question: 'You have only 4 months of runway and a small team. What is your priority?',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'build-multiple-features', label: 'Build multiple features', icon: <Code size={24} weight="duotone" /> },
              { value: 'hire-aggressively', label: 'Hire aggressively', icon: <Users size={24} weight="duotone" /> },
              { value: 'focus-core-value', label: 'Focus on core value proposition and revenue-generating experiments', icon: <Target size={24} weight="duotone" /> },
              { value: 'rebrand-product', label: 'Rebrand product', icon: <MegaphoneSimple size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-e4',
            question: 'As founder, you handle sales, product, and hiring simultaneously. How do you operate effectively?',
            helperText: 'Tests founder resourcefulness',
            isScenario: true,
            options: [
              { value: 'react-to-tasks', label: 'React to tasks as they come', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'work-longer-hours', label: 'Work longer hours only', icon: <Clock size={24} weight="duotone" /> },
              { value: 'prioritize-high-impact', label: 'Prioritize high-impact tasks aligned with survival and growth', icon: <ChartLineUp size={24} weight="duotone" /> },
              { value: 'delegate-everything', label: 'Delegate everything without oversight', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: SF-E5, SF-E6
      {
        id: 'founder-screen-3',
        initialChatText: "Finally, let's understand your AI strategy and leadership approach.",
        questions: [
          {
            id: 'sf-e5',
            question: 'AI tools can automate content, analytics, and support. How do you approach this?',
            helperText: 'Tests AI literacy',
            isScenario: true,
            options: [
              { value: 'avoid-ai-tools', label: 'Avoid AI tools', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'fully-rely-ai', label: 'Fully rely on AI without oversight', icon: <Brain size={24} weight="duotone" /> },
              { value: 'use-ai-validate', label: 'Use AI to improve productivity while validating outputs', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'replace-team', label: 'Replace team members', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-e6',
            question: 'You and your co-founder disagree on product direction. What do you do?',
            helperText: 'Tests leadership',
            isScenario: true,
            options: [
              { value: 'push-decision', label: 'Push your decision forcefully', icon: <Target size={24} weight="duotone" /> },
              { value: 'avoid-discussion', label: 'Avoid discussion', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'align-vision-validate', label: 'Align on long-term vision and validate direction through user data', icon: <Brain size={24} weight="duotone" /> },
              { value: 'escalate-investors', label: 'Escalate to investors', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level)
    '3-8': [
      // Screen 1: SF-M1, SF-M2
      {
        id: 'founder-screen-1',
        initialChatText: "Let's explore your scaling strategy and business fundamentals.",
        questions: [
          {
            id: 'sf-m1',
            question: 'You\'ve achieved early traction and consistent revenue growth. What is your next focus?',
            helperText: 'Tests venture building',
            isScenario: true,
            options: [
              { value: 'launch-multiple-products', label: 'Launch multiple new products', icon: <Code size={24} weight="duotone" /> },
              { value: 'raise-funds', label: 'Raise funds immediately', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'strengthen-distribution', label: 'Strengthen distribution channels, unit economics, and retention', icon: <ChartLineUp size={24} weight="duotone" /> },
              { value: 'increase-burn-rate', label: 'Increase burn rate', icon: <TrendUp size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-m2',
            question: 'CAC is rising and margins are shrinking. Your approach?',
            helperText: 'Tests business fundamentals',
            isScenario: true,
            options: [
              { value: 'increase-prices', label: 'Increase prices immediately', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'cut-marketing', label: 'Cut marketing entirely', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'optimize-channels-ltv', label: 'Optimize acquisition channels, LTV, pricing, and cost structure', icon: <Brain size={24} weight="duotone" /> },
              { value: 'ignore-temporarily', label: 'Ignore temporarily', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: SF-M3, SF-M4
      {
        id: 'founder-screen-2',
        initialChatText: "Now let's assess your leadership and strategic thinking.",
        questions: [
          {
            id: 'sf-m3',
            question: 'You\'re hiring your first senior leaders. What matters most?',
            helperText: 'Tests leadership',
            isScenario: true,
            options: [
              { value: 'hire-fast', label: 'Hire fast', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'hire-friends', label: 'Hire friends', icon: <Users size={24} weight="duotone" /> },
              { value: 'hire-culture-alignment', label: 'Hire for cultural alignment, ownership mindset, and complementary strengths', icon: <Target size={24} weight="duotone" /> },
              { value: 'outsource-leadership', label: 'Outsource leadership', icon: <Buildings size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-m4',
            question: 'A well-funded competitor enters your market. How do you respond?',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'panic-pivot', label: 'Panic and pivot immediately', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'slash-pricing', label: 'Slash pricing', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'double-down-differentiation', label: 'Double down on differentiation and strengthen customer relationships', icon: <Trophy size={24} weight="duotone" /> },
              { value: 'reduce-marketing', label: 'Reduce marketing', icon: <MegaphoneSimple size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: SF-M5, SF-M6
      {
        id: 'founder-screen-3',
        initialChatText: "Finally, let's understand your AI strategy and investor management.",
        questions: [
          {
            id: 'sf-m5',
            question: 'You want to scale customer acquisition without scaling team proportionally. How do you approach this?',
            helperText: 'Tests AI literacy',
            isScenario: true,
            options: [
              { value: 'increase-headcount', label: 'Increase headcount', icon: <Users size={24} weight="duotone" /> },
              { value: 'avoid-automation', label: 'Avoid automation risk', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'use-ai-efficiency', label: 'Use AI for personalization, analytics, and operational efficiency', icon: <Brain size={24} weight="duotone" /> },
              { value: 'cut-support', label: 'Cut support', icon: <Gear size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-m6',
            question: 'Investors push for aggressive expansion; data shows moderate sustainable growth. What do you do?',
            helperText: 'Tests leadership & strategic thinking',
            isScenario: true,
            options: [
              { value: 'follow-investor-pressure', label: 'Follow investor pressure blindly', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'ignore-investors', label: 'Ignore investors', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'present-data-backed-plan', label: 'Present data-backed plan balancing growth and sustainability', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'delay-communication', label: 'Delay communication', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level)
    '8+': [
      // Screen 1: SF-S1, SF-S2
      {
        id: 'founder-screen-1',
        initialChatText: "Let's explore your portfolio strategy and long-term vision.",
        questions: [
          {
            id: 'sf-s1',
            question: 'Your company now has 3 product lines. How do you allocate focus?',
            helperText: 'Tests venture building & strategic thinking',
            isScenario: true,
            options: [
              { value: 'equal-attention', label: 'Equal attention to all', icon: <Target size={24} weight="duotone" /> },
              { value: 'focus-legacy', label: 'Focus only on legacy product', icon: <Gear size={24} weight="duotone" /> },
              { value: 'allocate-strategic-fit', label: 'Allocate based on strategic fit, margin profile, and long-term potential', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'expand-randomly', label: 'Expand randomly', icon: <Rocket size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-s2',
            question: 'Market dynamics are shifting due to AI disruption. How do you respond?',
            helperText: 'Tests strategic thinking',
            isScenario: true,
            options: [
              { value: 'maintain-roadmap', label: 'Maintain existing roadmap', icon: <Path size={24} weight="duotone" /> },
              { value: 'react-impulsively', label: 'React impulsively', icon: <Lightning size={24} weight="duotone" /> },
              { value: 'reevaluate-positioning', label: 'Re-evaluate positioning, capabilities, and 3–5 year vision', icon: <Brain size={24} weight="duotone" /> },
              { value: 'cut-costs-only', label: 'Cut costs only', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 2: SF-S3, SF-S4
      {
        id: 'founder-screen-2',
        initialChatText: "Now let's assess your organizational leadership and capital allocation.",
        questions: [
          {
            id: 'sf-s3',
            question: 'Company has grown from 20 to 250 employees. What becomes critical?',
            helperText: 'Tests leadership',
            isScenario: true,
            options: [
              { value: 'founder-all-decisions', label: 'Founder makes all decisions', icon: <Users size={24} weight="duotone" /> },
              { value: 'minimal-process', label: 'Minimal process', icon: <Gear size={24} weight="duotone" /> },
              { value: 'define-org-structure', label: 'Define org structure, accountability systems, and leadership layers', icon: <Buildings size={24} weight="duotone" /> },
              { value: 'increase-meetings', label: 'Increase meetings', icon: <Clock size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-s4',
            question: 'You\'ve raised significant funding. Best approach?',
            helperText: 'Tests business fundamentals',
            isScenario: true,
            options: [
              { value: 'spend-aggressively', label: 'Spend aggressively for growth', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'protect-capital', label: 'Protect capital without investing', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'allocate-capital-roi', label: 'Allocate capital based on ROI, strategic advantage, and risk profile', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'increase-burn-outpace', label: 'Increase burn to outpace competitors', icon: <Rocket size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 3: SF-S5, SF-S6
      {
        id: 'founder-screen-3',
        initialChatText: "Finally, let's understand your AI transformation strategy and culture building.",
        questions: [
          {
            id: 'sf-s5',
            question: 'Board wants company-wide AI transformation. How do you approach this?',
            helperText: 'Tests AI literacy',
            isScenario: true,
            options: [
              { value: 'implement-ai-everywhere', label: 'Implement AI everywhere immediately', icon: <Brain size={24} weight="duotone" /> },
              { value: 'resist-change', label: 'Resist change', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'identify-pilot-scale', label: 'Identify high-impact areas, pilot initiatives, measure ROI, then scale', icon: <Target size={24} weight="duotone" /> },
              { value: 'replace-teams', label: 'Replace teams entirely', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'sf-s6',
            question: 'As organization scales, culture begins to fragment. What is your priority?',
            helperText: 'Tests leadership',
            isScenario: true,
            options: [
              { value: 'focus-revenue-only', label: 'Focus only on revenue', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'let-culture-evolve', label: 'Let culture evolve randomly', icon: <Users size={24} weight="duotone" /> },
              { value: 'define-values-principles', label: 'Define values, leadership principles, and accountability mechanisms', icon: <Trophy size={24} weight="duotone" /> },
              { value: 'increase-monitoring', label: 'Increase monitoring', icon: <ChartBar size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  },

  'tech': {
    // 0-3 years experience (Entry level) - Questions from document
    '0-3': [
      // Screen 3: Tech Questions 1-2
      {
        id: 'tech-screen-1',
        initialChatText: "Let's explore your product thinking and business impact awareness.",
        questions: [
          {
            id: 'tm-e1',
            question: 'You built a feature that took significant effort. After launch, usage is very low. The Product Manager hasn\'t raised concerns yet. What do you do?',
            helperText: 'Tests product thinking and ownership',
            isScenario: true,
            options: [
              { value: 'move-next-task', label: 'Move to the next task since launch is complete', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'suggest-features', label: 'Suggest adding more features to improve engagement', icon: <Code size={24} weight="duotone" /> },
              { value: 'review-data', label: 'Review usage data and speak to Product about potential issues', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'blame-marketing', label: 'Assume marketing didn\'t promote it well', icon: <MegaphoneSimple size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-e2',
            question: 'You reduced server response time by 25%, but users haven\'t directly complained about speed before. How do you evaluate the value of your work?',
            helperText: 'Tests business impact awareness',
            isScenario: true,
            options: [
              { value: 'code-quality', label: 'It improves code quality only', icon: <Code size={24} weight="duotone" /> },
              { value: 'ux-cost', label: 'It may improve user experience and reduce infra costs', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'deployment', label: 'It makes deployment easier', icon: <Gear size={24} weight="duotone" /> },
              { value: 'doesnt-matter', label: 'It doesn\'t matter if users didn\'t complain', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 4: Tech Questions 3-4
      {
        id: 'tech-screen-2',
        initialChatText: "Now let's assess your execution approach and prioritization thinking.",
        questions: [
          {
            id: 'tm-e3',
            question: 'Two days before release, you discover a bug that may impact 5% of users. What is the best action?',
            helperText: 'Tests execution and accountability',
            isScenario: true,
            options: [
              { value: 'ignore-small', label: 'Ignore since impact is small', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'inform-stakeholders', label: 'Inform stakeholders and suggest fix vs delay trade-off', icon: <Users size={24} weight="duotone" /> },
              { value: 'fix-quietly', label: 'Fix quietly without informing anyone', icon: <CheckCircle size={24} weight="duotone" /> },
              { value: 'delay-release', label: 'Delay release without explanation', icon: <Clock size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-e4',
            question: 'You have: Task A: Refactor messy code (no immediate visible impact). Task B: Add minor UI feature requested by Sales. Both take equal time. How do you decide?',
            helperText: 'Tests prioritization thinking',
            isScenario: true,
            options: [
              { value: 'technically-interesting', label: 'Choose what is technically interesting', icon: <Code size={24} weight="duotone" /> },
              { value: 'business-impact', label: 'Evaluate business impact and urgency', icon: <Target size={24} weight="duotone" /> },
              { value: 'always-sales', label: 'Always prioritize Sales', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'always-refactor', label: 'Always prioritize refactoring', icon: <Gear size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 5: Tech Questions 5-6
      {
        id: 'tech-screen-3',
        initialChatText: "Finally, let's understand your AI literacy and ownership mindset.",
        questions: [
          {
            id: 'tm-e5',
            question: 'Your manager suggests adopting an AI coding assistant for the team. What should you evaluate?',
            helperText: 'Tests AI literacy in business context',
            options: [
              { value: 'competitor-usage', label: 'Whether competitors use it', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'productivity-cost', label: 'Whether it improves productivity enough to justify cost', icon: <Target size={24} weight="duotone" /> },
              { value: 'trending', label: 'Whether it\'s trending', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'junior-likes', label: 'Whether junior engineers like it', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-e6',
            question: 'A production issue occurred due to a decision you made, though it wasn\'t fully your fault. What do you do?',
            helperText: 'Tests ownership mindset',
            isScenario: true,
            options: [
              { value: 'highlight-others', label: 'Highlight that others were involved', icon: <Users size={24} weight="duotone" /> },
              { value: 'take-ownership', label: 'Take ownership and help resolve it', icon: <Trophy size={24} weight="duotone" /> },
              { value: 'stay-silent', label: 'Stay silent unless asked', icon: <Clock size={24} weight="duotone" /> },
              { value: 'escalate-blame', label: 'Escalate blame', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 3-8 years experience (Mid level) - Questions from document
    '3-8': [
      // Screen 3: Tech Questions 1-2
      {
        id: 'tech-screen-1',
        initialChatText: "Let's explore your feature ROI evaluation and cost management thinking.",
        questions: [
          {
            id: 'tm-m1',
            question: 'A new feature requires 6 weeks of engineering effort and may increase retention by 3%. What matters most before committing?',
            helperText: 'Tests feature ROI evaluation',
            isScenario: true,
            options: [
              { value: 'technical-feasibility', label: 'Technical feasibility', icon: <Code size={24} weight="duotone" /> },
              { value: 'revenue-opportunity', label: 'Revenue impact vs opportunity cost', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'team-excitement', label: 'Team excitement', icon: <Users size={24} weight="duotone" /> },
              { value: 'competitor-parity', label: 'Competitor parity', icon: <Target size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-m2',
            question: 'Cloud costs increased 50% year-over-year. Growth is only 15%. What\'s your first strategic step?',
            helperText: 'Tests cost escalation management',
            isScenario: true,
            options: [
              { value: 'immediate-overhaul', label: 'Immediate infra overhaul', icon: <Gear size={24} weight="duotone" /> },
              { value: 'analyze-drivers', label: 'Analyze cost drivers and inefficiencies', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'reduce-features', label: 'Reduce product features', icon: <Code size={24} weight="duotone" /> },
              { value: 'wait-cfo', label: 'Wait for CFO direction', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 4: Tech Questions 3-4
      {
        id: 'tech-screen-2',
        initialChatText: "Now let's assess your cross-functional collaboration and tech debt management.",
        questions: [
          {
            id: 'tm-m3',
            question: 'Sales closed a major deal promising a custom feature not on roadmap. What do you do?',
            helperText: 'Tests sales vs product conflict resolution',
            isScenario: true,
            options: [
              { value: 'build-immediately', label: 'Build immediately', icon: <Code size={24} weight="duotone" /> },
              { value: 'reject-firmly', label: 'Reject firmly', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'evaluate-align', label: 'Evaluate revenue impact and align with Product roadmap', icon: <Target size={24} weight="duotone" /> },
              { value: 'escalate-conflict', label: 'Escalate conflict', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-m4',
            question: 'Engineering wants refactoring. Leadership wants faster feature releases. How do you respond?',
            helperText: 'Tests tech debt vs speed balance',
            isScenario: true,
            options: [
              { value: 'prioritize-features', label: 'Prioritize features always', icon: <Code size={24} weight="duotone" /> },
              { value: 'balance-debt', label: 'Balance short-term delivery with planned debt reduction', icon: <Target size={24} weight="duotone" /> },
              { value: 'focus-health', label: 'Focus only on engineering health', icon: <Gear size={24} weight="duotone" /> },
              { value: 'ignore-pressure', label: 'Ignore leadership pressure', icon: <XCircle size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 5: Tech Questions 5-6
      {
        id: 'tech-screen-3',
        initialChatText: "Finally, let's understand your AI investment decisions and resource allocation.",
        questions: [
          {
            id: 'tm-m5',
            question: 'Leadership wants to add AI chatbot to product. Your evaluation criteria?',
            helperText: 'Tests AI investment decision making',
            options: [
              { value: 'trend-value', label: 'Trend value', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'pain-point-roi', label: 'Clear user pain point + ROI projection', icon: <Target size={24} weight="duotone" /> },
              { value: 'competitive-pressure', label: 'Competitive pressure', icon: <ChartBar size={24} weight="duotone" /> },
              { value: 'ease-implementation', label: 'Ease of implementation', icon: <Code size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-m6',
            question: 'You have limited engineering bandwidth. Options: Infra optimization, New revenue feature, Internal tooling. How do you decide?',
            helperText: 'Tests resource allocation thinking',
            isScenario: true,
            options: [
              { value: 'highest-impact', label: 'Choose highest visible impact', icon: <Target size={24} weight="duotone" /> },
              { value: 'growth-priorities', label: 'Align with company growth priorities', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'technically-complex', label: 'Choose technically complex work', icon: <Code size={24} weight="duotone" /> },
              { value: 'team-vote', label: 'Let team vote', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ],
    // 8+ years experience (Senior level) - Questions from document
    '8+': [
      // Screen 3: Tech Questions 1-2
      {
        id: 'tech-screen-1',
        initialChatText: "Let's explore your strategic trade-offs and capital allocation thinking.",
        questions: [
          {
            id: 'tm-s1',
            question: 'You must choose between: Improving reliability (reduces churn risk) OR Launching a revenue-driving feature. Decision should be based on?',
            helperText: 'Tests strategic trade-off thinking',
            isScenario: true,
            options: [
              { value: 'immediate-revenue', label: 'Immediate revenue only', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'long-term-strategy', label: 'Long-term company strategy + risk analysis', icon: <Target size={24} weight="duotone" /> },
              { value: 'engineering-preference', label: 'Engineering preference', icon: <Code size={24} weight="duotone" /> },
              { value: 'ceo-urgency', label: 'CEO urgency', icon: <Clock size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-s2',
            question: 'You receive ₹3 Cr for engineering improvement. Best approach?',
            helperText: 'Tests capital allocation thinking',
            isScenario: true,
            options: [
              { value: 'hire-aggressively', label: 'Hire aggressively', icon: <Users size={24} weight="duotone" /> },
              { value: 'invest-ai', label: 'Invest entirely in AI', icon: <Brain size={24} weight="duotone" /> },
              { value: 'allocate-roi', label: 'Allocate across people, infra, and automation based on ROI', icon: <Target size={24} weight="duotone" /> },
              { value: 'preserve-capital', label: 'Preserve capital', icon: <CurrencyInr size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 4: Tech Questions 3-4
      {
        id: 'tech-screen-2',
        initialChatText: "Now let's assess your understanding of engineering as competitive advantage and scalability.",
        questions: [
          {
            id: 'tm-s3',
            question: 'When does tech become a moat?',
            helperText: 'Tests understanding of engineering as competitive advantage',
            options: [
              { value: 'complex-architecture', label: 'When architecture is complex', icon: <Code size={24} weight="duotone" /> },
              { value: 'switching-cost-value', label: 'When it increases switching cost and user value', icon: <Target size={24} weight="duotone" /> },
              { value: 'high-infra-cost', label: 'When infra cost is high', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'large-team', label: 'When team size grows', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-s4',
            question: 'You expect 5x user growth in 2 years but revenue is tight today. What\'s the best strategy?',
            helperText: 'Tests long-term scalability thinking',
            isScenario: true,
            options: [
              { value: 'overbuild-now', label: 'Overbuild now', icon: <Code size={24} weight="duotone" /> },
              { value: 'modular-incremental', label: 'Build modular systems that scale incrementally', icon: <Gear size={24} weight="duotone" /> },
              { value: 'ignore-projections', label: 'Ignore future projections', icon: <XCircle size={24} weight="duotone" /> },
              { value: 'freeze-innovation', label: 'Freeze innovation', icon: <Clock size={24} weight="duotone" /> }
            ]
          }
        ]
      },
      // Screen 5: Tech Questions 5-6
      {
        id: 'tech-screen-3',
        initialChatText: "Finally, let's understand your AI strategy and portfolio balancing.",
        questions: [
          {
            id: 'tm-s5',
            question: 'How do you decide if AI should be core product vs supporting feature?',
            helperText: 'Tests AI as strategy thinking',
            options: [
              { value: 'industry-trend', label: 'Industry trend', icon: <TrendUp size={24} weight="duotone" /> },
              { value: 'strategic-differentiation', label: 'Strategic differentiation potential', icon: <Target size={24} weight="duotone" /> },
              { value: 'investor-excitement', label: 'Investor excitement', icon: <CurrencyInr size={24} weight="duotone" /> },
              { value: 'team-expertise', label: 'Team expertise', icon: <Users size={24} weight="duotone" /> }
            ]
          },
          {
            id: 'tm-s6',
            question: 'How should engineering capacity typically be allocated?',
            helperText: 'Tests portfolio balancing thinking',
            options: [
              { value: 'all-features', label: '100% new features', icon: <Code size={24} weight="duotone" /> },
              { value: 'balanced-allocation', label: 'Balanced between growth, stability, and innovation', icon: <Target size={24} weight="duotone" /> },
              { value: 'mostly-refactoring', label: 'Mostly refactoring', icon: <Gear size={24} weight="duotone" /> },
              { value: 'engineering-interest', label: 'Based on engineering interest', icon: <Users size={24} weight="duotone" /> }
            ]
          }
        ]
      }
    ]
  }
};

// Helper function to map frontend experience values to document experience levels
export const mapExperienceToLevel = (experience) => {
  // Frontend and Document now use the same values: '0-3', '3-8', '8+'
  // Return as-is since they match, or default to mid-level if unknown
  if (experience === '0-3' || experience === '3-8' || experience === '8+') {
    return experience;
  }
  // Default to mid-level if unknown
  return '3-8';
};

// Helper to check if all questions in a screen are answered
export const isScreenComplete = (screenId, responses) => {
  // Implementation will be handled in the orchestrator
  return true;
};
